import { ProductDraft } from "@/types/general";
import { refinements } from "@helpers/refinements";
import {
    CustomFieldsInjectTyping,
    withCustomFields,
} from "@reusables/BaseCustomFieldsSection/logic";
import { z } from "zod";

const MAX_FILE_SIZE = import.meta.env.VITE_MAX_PRODUCT_IMAGE_SIZE;

const nullableNonNegativeNumber = z.preprocess(
    (value) => (value === "" || value === undefined || value === null ? null : Number(value)),
    z.number().min(0).nullable()
);

const nullableString = z.preprocess(
    (value) => (value === "" || value === undefined || value === null ? null : value),
    z.string().trim().nullable()
);

const imageSchema = z
    .any()
    .optional()
    .refine(
        (value) =>
            typeof value === "string" || refinements.fileSize(MAX_FILE_SIZE, "MB").refine(value),
        refinements.fileSize(MAX_FILE_SIZE, "MB").message
    )
    .refine(
        (value) => typeof value === "string" || refinements.imageFormat.refine(value),
        refinements.imageFormat.message
    );

const optionalCheckbox = z
    .boolean()
    .nullish()
    .transform((value) => value ?? false);

const draftFormBaseSchema = z
    .object({
        general: z.object({
            name: z.string().trim().min(1),
            code: z
                .string()
                .nullish()
                .transform((value) => value?.trim() ?? "")
                .refine(refinements.noSpaces.refine, refinements.noSpaces.message),
            supplier_product_code: nullableString,
            barcode: nullableString.refine(
                refinements.noSpaces.refine,
                refinements.noSpaces.message
            ),
            category: z
                .object({
                    id: z.number(),
                    name: z.string(),
                    code: z.string(),
                    is_service: z.boolean(),
                })
                .nullable(),
            unit: z
                .object({
                    id: z.number(),
                    name: z.string(),
                    code: z.string(),
                })
                .nullable(),
            default_location: z
                .object({
                    store: z.object({ id: z.number(), name: z.string() }),
                    section: z.object({ id: z.number(), name: z.string() }).nullable(),
                })
                .nullable(),
            supplier: z
                .object({
                    id: z.number(),
                    name: z.string(),
                    code: z.string(),
                })
                .nullable(),
            tax: z
                .object({
                    id: z.number(),
                    name: z.string(),
                    rate: z.number(),
                })
                .nullable(),
            unit_equivalents: z.array(
                z.object({
                    unit_id: z.coerce.number().positive(),
                    quantity: z.coerce.number().positive(),
                })
            ),
        }),
        prices: z.object({
            purchase_price: nullableNonNegativeNumber,
            selling_price: nullableNonNegativeNumber,
            extra_cost: nullableNonNegativeNumber,
        }),
        inventory: z.object({
            min_inventory_quantity: nullableNonNegativeNumber,
            min_purchase_quantity: nullableNonNegativeNumber,
            min_sale_quantity: nullableNonNegativeNumber,
        }),
        weights_and_sizes: z.object({
            weight: nullableNonNegativeNumber,
            CBM: nullableNonNegativeNumber,
            width: nullableNonNegativeNumber,
            height: nullableNonNegativeNumber,
            length: nullableNonNegativeNumber,
        }),
        options: z.object({
            is_RFID: optionalCheckbox,
            is_batch_number: optionalCheckbox,
            is_serial_number: optionalCheckbox,
            is_component: optionalCheckbox,
        }),
        description: z.object({
            image: imageSchema,
            description: nullableString,
        }),
        is_service: optionalCheckbox,
    })
    .superRefine((data, ctx) => {
        if (!data.is_service && !data.general.default_location) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: { i18n: "general.validation.global.required" },
                path: ["general", "default_location"],
            });
        }

        const equivalentUnitIds = data.general.unit_equivalents.map((item) => item.unit_id);
        if (new Set(equivalentUnitIds).size !== equivalentUnitIds.length) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: { i18n: "productDrafts.editor.validation.uniqueEquivalentUnits" },
                path: ["general", "unit_equivalents"],
            });
        }

        if (data.general.unit && equivalentUnitIds.includes(data.general.unit.id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: { i18n: "productDrafts.editor.validation.baseUnitEquivalent" },
                path: ["general", "unit_equivalents"],
            });
        }
    });

export const productDraftFormSchema = withCustomFields(draftFormBaseSchema);

export type ProductDraftFormValues = z.infer<typeof productDraftFormSchema>;

export type ProductDraftFormDirtyFields = Partial<
    Record<keyof ProductDraftFormValues, boolean | Record<string, unknown> | unknown[]>
>;

export type ProductDraftFormDefaults = ProductDraftFormValues & CustomFieldsInjectTyping;

export function assertValidDefaultLocation(product: ProductDraft.Extended): void {
    const location = product.product.default_location;
    if (!location) return;

    if (!location.store || !Number.isInteger(location.store.id) || !location.store.name) {
        throw new Error(`Product draft ${product.id} has a malformed default location`);
    }

    if (location.section && (!Number.isInteger(location.section.id) || !location.section.name)) {
        throw new Error(`Product draft ${product.id} has a malformed default location section`);
    }
}
